/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, Typography, IconButton, Button,
  Drawer, List, ListItemButton, ListItemText, Collapse,
  Chip, Menu, MenuItem, Paper,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';

import CheckIcon from '@mui/icons-material/Check';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import PhoneIcon from '@mui/icons-material/Phone';
import TranslateIcon from '@mui/icons-material/Translate';
import { Avatar } from '@mui/material';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { reapplyGoogleTranslate } from '../hooks/useGoogleTranslate';
import { enablePushNotifications } from '../pushNotifications';

const GOLD = '#d4af37';

const getMenuStructure = (t: (key: string) => string) => [
  { labelKey: 'menu.home', label: t('menu.home'), to: '/' },
  {
    labelKey: 'menu.poojas', label: t('menu.poojas'), to: '/poojas',
    sub: [
      { labelKey: 'submenu.booking', label: t('submenu.booking'), to: '/poojas/booking' },
      { labelKey: 'submenu.dailyPoojas', label: t('submenu.dailyPoojas'), to: '/poojas/daily' },
      { labelKey: 'submenu.festivalPoojas', label: t('submenu.festivalPoojas'), to: '/poojas/festival' },
      { labelKey: 'submenu.specialPoojas', label: t('submenu.specialPoojas'), to: '/poojas/special' },
      { labelKey: 'submenu.vazhipadList', label: t('submenu.vazhipadList'), to: '/poojas/vazhipad' },
    ],
  },
  {
    labelKey: 'menu.donate', label: t('menu.donate'), to: '/donate',
    sub: [
      { labelKey: 'submenu.donateOverview', label: t('submenu.donateOverview'), to: '/donate' },
      { labelKey: 'submenu.onlineDonation', label: t('submenu.onlineDonation'), to: '/donate/online' },
      { labelKey: 'submenu.annadanam', label: t('submenu.annadanam'), to: '/donate/annadanam' },
      { labelKey: 'submenu.renovationFund', label: t('submenu.renovationFund'), to: '/donate/renovation' },
      { labelKey: 'submenu.festivalContribution', label: t('submenu.festivalContribution'), to: '/donate/festival' },
    ],
  },
  {
    labelKey: 'menu.gallery', label: t('menu.gallery'), to: '/gallery',
    sub: [
      { labelKey: 'submenu.galleryOverview', label: t('submenu.galleryOverview'), to: '/gallery' },
      { labelKey: 'submenu.photos', label: t('submenu.photos'), to: '/gallery/photos' },
      { labelKey: 'submenu.videos', label: t('submenu.videos'), to: '/gallery/videos' },
      { labelKey: 'submenu.events', label: t('submenu.events'), to: '/gallery/events' },
      { labelKey: 'submenu.templePremises', label: t('submenu.templePremises'), to: '/gallery/premises' },
    ],
  },
  {
    labelKey: 'menu.news', label: t('menu.news'), to: '/news',
    sub: [
      { labelKey: 'submenu.newsOverview', label: t('submenu.newsOverview'), to: '/news' },
      { labelKey: 'submenu.templeNews', label: t('submenu.templeNews'), to: '/news/news' },
      { labelKey: 'submenu.announcements', label: t('submenu.announcements'), to: '/news/announcements' },
      { labelKey: 'submenu.upcomingEvents', label: t('submenu.upcomingEvents'), to: '/news/upcoming' },
      { labelKey: 'submenu.pastEvents', label: t('submenu.pastEvents'), to: '/news/past' },
      { labelKey: 'submenu.notices', label: t('submenu.notices'), to: '/news/notices' },
    ],
  },
  {
    labelKey: 'menu.about', label: t('menu.about'), to: '/about',
    sub: [
      { labelKey: 'submenu.aboutOverview', label: t('submenu.aboutOverview'), to: '/about' },
      { labelKey: 'submenu.templeHistory', label: t('submenu.templeHistory'), to: '/about/history' },
      { labelKey: 'submenu.deities', label: t('submenu.deities'), to: '/about/deities' },
      { labelKey: 'submenu.festivals', label: t('submenu.festivals'), to: '/about/festivals' },
      { labelKey: 'submenu.administration', label: t('submenu.administration'), to: '/about/administration' },
      { labelKey: 'submenu.rulesTimings', label: t('submenu.rulesTimings'), to: '/about/rules' },
    ],
  },
  {
    labelKey: 'menu.nearby', label: t('menu.nearby'), to: '/nearby',
    sub: [
      { labelKey: 'submenu.nearbyOverview', label: t('submenu.nearbyOverview'), to: '/nearby' },
      { labelKey: 'submenu.templesNearby', label: t('submenu.templesNearby'), to: '/nearby/temples' },
      { labelKey: 'submenu.boating', label: t('submenu.boating'), to: '/nearby/boating' },
      { labelKey: 'submenu.viewpoints', label: t('submenu.viewpoints'), to: '/nearby/viewpoints' },
      { labelKey: 'submenu.heritageSites', label: t('submenu.heritageSites'), to: '/nearby/heritage' },
      { labelKey: 'submenu.beaches', label: t('submenu.beaches'), to: '/nearby/beaches' },
      { labelKey: 'submenu.activities', label: t('submenu.activities'), to: '/nearby/activities' },
    ],
  },
  {
    labelKey: 'menu.contact', label: t('menu.contact'), to: '/contact',
    sub: [
      { labelKey: 'submenu.contactOverview', label: t('submenu.contactOverview'), to: '/contact' },
      { labelKey: 'submenu.contactInfo', label: t('submenu.contactInfo'), to: '/contact/info' },
      { labelKey: 'submenu.mapDirections', label: t('submenu.mapDirections'), to: '/contact/map' },
      { labelKey: 'submenu.feedbackForm', label: t('submenu.feedbackForm'), to: '/contact/feedback' },
    ],
  },
];

export default function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState<Record<string, boolean>>({});
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const { language, setLanguage, t, translateLanguage, setTranslateLanguage, availableLanguages } = useLanguage();
  const { user, logout, openLoginModal } = useAuth();
  const closeTimerRef = React.useRef<number | null>(null);
  const location = useLocation();
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => { reapplyGoogleTranslate(); }, [location.pathname]);

  const MENU = getMenuStructure(t);

  const isActive = (to?: string) => {
    if (!to) return false;
    const p = location.pathname || '/';
    if (to === '/') return p === '/';
    return p === to || p.startsWith(to + '/');
  };

  const handleMenuEnter = (label: string) => {
    if (closeTimerRef.current) { window.clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    setOpenSubmenu(label);
  };
  const handleMenuLeave = () => {
    closeTimerRef.current = window.setTimeout(() => setOpenSubmenu(null), 200);
  };

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          const doc = document.documentElement;
          const total = doc.scrollHeight - doc.clientHeight;
          setProgress(total > 0 ? Math.min(100, Math.max(0, (window.scrollY / total) * 100)) : 0);
          ticking = false;
        });
        ticking = true;
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeDrawer = () => { setDrawerOpen(false); setMobileOpen({}); };

  const handleEnableNotifications = async () => {
    try {
      setPushBusy(true);
      const res = await enablePushNotifications();
      if (res.status === 'enabled') alert(t('notifications.enabled'));
      else if (res.status === 'denied' || res.status === 'blocked') alert(t('notifications.denied'));
      else if (res.status === 'missing-vapid-key') alert(t('notifications.missingVapid'));
      else if (res.status === 'unsupported') alert(t('notifications.unsupported'));
      else alert(t('notifications.error'));
    } finally { setPushBusy(false); }
  };

  /* ── Shared menu styles ── */
  const menuPaperSx = {
    bgcolor: 'rgba(8,6,4,0.97)',
    backdropFilter: 'blur(28px)',
    border: `1px solid ${GOLD}20`,
    borderTop: `2px solid ${GOLD}`,
    borderRadius: 3,
    mt: 1,
    boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
    minWidth: 200,
  };

  return (
    <>
      <AppBar position="fixed" elevation={0} sx={{
        zIndex: (theme) => theme.zIndex.drawer + 20,
        background: scrolled
          ? 'linear-gradient(180deg, #0a0706 0%, #0f0c08 60%, #110e09 100%)'
          : 'transparent',
        backdropFilter: scrolled ? 'blur(24px) saturate(180%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(24px) saturate(180%)' : 'none',
        borderBottom: scrolled ? `1.5px solid ${GOLD}22` : 'none',
        boxShadow: scrolled
          ? `0 4px 30px rgba(0,0,0,0.6), 0 1px 0 ${GOLD}15, inset 0 -1px 0 ${GOLD}10`
          : 'none',
        transition: 'all 400ms cubic-bezier(0.4,0,0.2,1)',
        '&::before': scrolled ? {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: `radial-gradient(ellipse 80% 50% at 50% 0%, ${GOLD}08 0%, transparent 60%)`,
          pointerEvents: 'none',
        } : {},
        '&::after': scrolled ? {
          content: '""',
          position: 'absolute',
          bottom: -1, left: '10%', right: '10%', height: '1px',
          background: `linear-gradient(90deg, transparent, ${GOLD}40, ${GOLD}60, ${GOLD}40, transparent)`,
          pointerEvents: 'none',
        } : {},
      }}>
        {/* Scroll progress */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, height: scrolled ? 2.5 : 2,
          width: `${progress}%`,
          background: `linear-gradient(90deg, #b8860b, ${GOLD}, #e5c158, ${GOLD}, #b8860b)`,
          boxShadow: scrolled ? `0 0 16px ${GOLD}70, 0 0 4px ${GOLD}90` : `0 0 10px ${GOLD}60`,
          transition: 'width 80ms linear, height 300ms ease, box-shadow 300ms ease', zIndex: 1500,
        }} />

        <Box sx={{ px: { xs: 1, sm: 2, md: 3, lg: 4 }, maxWidth: 1480, mx: 'auto', width: '100%' }}>

          {/* ═══ TOP UTILITY BAR (desktop only) ═══ */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center', justifyContent: 'space-between',
            py: scrolled ? 0.5 : 0.8,
            borderBottom: `1px solid ${scrolled ? GOLD + '10' : 'rgba(255,255,255,0.035)'}`,
            transition: 'all 350ms cubic-bezier(0.4,0,0.2,1)',
          }}>
            {/* Left — contact pills */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* Temple Timings */}
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.6,
                  px: 1.3, py: 0.4, borderRadius: 5,
                  bgcolor: 'rgba(212,175,55,0.035)',
                  border: '1px solid rgba(212,175,55,0.06)',
                  transition: 'all 200ms',
                  cursor: 'default',
                  '&:hover': { bgcolor: 'rgba(212,175,55,0.08)', borderColor: 'rgba(212,175,55,0.15)' },
                }}>
                <AccessTimeIcon sx={{ fontSize: 15, color: GOLD }} />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.65)', fontWeight: 600, lineHeight: 1.3, letterSpacing: 0.15 }}>
                    Kotta: 6:00 AM – 11:00 AM
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.65)', fontWeight: 600, lineHeight: 1.3, letterSpacing: 0.15 }}>
                    Kovilakam: 5:30 AM – 8:30 AM, 5:30 PM – 7:30 PM
                  </Typography>
                </Box>
              </Box>
              {[
                { icon: <EmailIcon sx={{ fontSize: 15, color: GOLD }} />, text: t('header.email'), href: `mailto:${t('header.email')}` },
                { icon: <PhoneIcon sx={{ fontSize: 15, color: GOLD }} />, text: 'Contact Us', href: 'tel:+919995939303' },
              ].map((c, i) => (
                <Box
                  key={i}
                  component={c.href ? 'a' : 'div'}
                  href={c.href}
                  sx={{
                  display: 'flex', alignItems: 'center', gap: 0.6,
                  px: 1.3, py: 0.4, borderRadius: 5,
                  bgcolor: 'rgba(212,175,55,0.035)',
                  border: '1px solid rgba(212,175,55,0.06)',
                  transition: 'all 200ms',
                  textDecoration: 'none',
                  cursor: c.href ? 'pointer' : 'default',
                  '&:hover': { bgcolor: 'rgba(212,175,55,0.08)', borderColor: 'rgba(212,175,55,0.15)' },
                }}>
                  {c.icon}
                  <Typography sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.65)', fontWeight: 500, letterSpacing: 0.15 }}>
                    {c.text}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Right — socials, lang, chips, auth */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              {/* Social icons */}
              {[
                { I: FacebookIcon, h: 'https://facebook.com' },
                { I: InstagramIcon, h: 'https://instagram.com' },
                { I: YouTubeIcon, h: 'https://www.youtube.com/@srikottakovilakamtemple' },
                { I: TwitterIcon, h: 'https://twitter.com' },
              ].map((s, i) => (
                <IconButton key={i} component="a" href={s.h} target="_blank" rel="noopener noreferrer" size="small"
                  sx={{ color: 'rgba(255,255,255,0.45)', p: 0.4, '&:hover': { color: GOLD }, transition: 'color 200ms' }}>
                  <s.I sx={{ fontSize: 17 }} />
                </IconButton>
              ))}

              <Box sx={{ width: 1, height: 16, bgcolor: 'rgba(255,255,255,0.06)', mx: 0.5 }} />

              {/* Language */}
              {(() => {
                const activeLang = availableLanguages.find(l => l.code === translateLanguage) || availableLanguages[0];
                return (
              <Box
                className="notranslate"
                onClick={(e) => setLangAnchor(e.currentTarget)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.6,
                  cursor: 'pointer', px: 1, py: 0.4,
                  borderRadius: '12px',
                  bgcolor: Boolean(langAnchor) ? `${GOLD}15` : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${Boolean(langAnchor) ? GOLD + '30' : 'rgba(255,255,255,0.06)'}`,
                  '&:hover': { bgcolor: `${GOLD}12`, borderColor: `${GOLD}25` },
                  transition: 'all 200ms',
                }}>
                <TranslateIcon sx={{ fontSize: 15, color: GOLD, opacity: 0.85 }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff', lineHeight: 1, whiteSpace: 'nowrap' }}>
                  {activeLang.nativeLabel}
                </Typography>
                <ExpandMoreIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', ml: -0.2, transition: 'transform 200ms', transform: Boolean(langAnchor) ? 'rotate(180deg)' : 'rotate(0deg)' }} />
              </Box>
                );
              })()}
              <Menu anchorEl={langAnchor} open={Boolean(langAnchor)} onClose={() => setLangAnchor(null)}
                TransitionProps={{ timeout: 200 }}
                PaperProps={{ sx: {
                  bgcolor: 'rgba(12,10,8,0.98)',
                  backdropFilter: 'blur(32px)',
                  border: `1px solid ${GOLD}20`,
                  borderRadius: '16px',
                  mt: 1.5,
                  boxShadow: `0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px ${GOLD}10`,
                  minWidth: 260, maxHeight: 480, overflowY: 'auto',
                  p: 0,
                  '&::-webkit-scrollbar': { width: 4 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: `${GOLD}35`, borderRadius: 4 },
                } }}>
                {/* Header */}
                <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${GOLD}12` }}>
                  <TranslateIcon sx={{ fontSize: 18, color: GOLD }} />
                  <Typography className="notranslate" sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>
                    Select Language
                  </Typography>
                </Box>
                <Box sx={{ p: 0.6 }}>
                {availableLanguages.map((lang, idx) => {
                  const isActive = translateLanguage === lang.code;
                  return (
                  <MenuItem key={lang.code} className="notranslate"
                    onClick={() => { setTranslateLanguage(lang.code); setLangAnchor(null); }}
                    sx={{
                      color: '#fff', py: 0.9, px: 1.4, fontSize: '0.84rem',
                      borderRadius: '10px', mb: 0.2,
                      display: 'flex', alignItems: 'center', gap: 1.2,
                      bgcolor: isActive ? `${GOLD}14` : 'transparent',
                      border: isActive ? `1px solid ${GOLD}25` : '1px solid transparent',
                      '&:hover': { bgcolor: isActive ? `${GOLD}20` : 'rgba(255,255,255,0.05)' },
                      transition: 'all 150ms',
                    }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '8px', flexShrink: 0,
                      background: isActive ? `linear-gradient(135deg, ${GOLD}, #c9a020)` : 'rgba(255,255,255,0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: isActive ? 'none' : '1px solid rgba(255,255,255,0.06)',
                      transition: 'all 200ms',
                    }}>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: isActive ? '#000' : 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {lang.code}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#fff' : 'rgba(255,255,255,0.85)', lineHeight: 1.3 }}>
                        {lang.nativeLabel}
                      </Typography>
                      {lang.code !== 'en' && (
                        <Typography sx={{ fontSize: '0.62rem', color: isActive ? GOLD : 'rgba(255,255,255,0.3)', lineHeight: 1.2, fontWeight: 500 }}>
                          {lang.label}
                        </Typography>
                      )}
                    </Box>
                    {isActive && (
                      <Box sx={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                        bgcolor: `${GOLD}20`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <CheckIcon sx={{ fontSize: 13, color: GOLD }} />
                      </Box>
                    )}
                  </MenuItem>
                  );
                })}
                </Box>
              </Menu>

              <Box sx={{ width: 1, height: 16, bgcolor: 'rgba(255,255,255,0.06)', mx: 0.5 }} />

              {/* Action chips */}
              <Chip label={t('header.donate')} size="small"
                icon={<FavoriteIcon sx={{ color: `${GOLD} !important`, fontSize: 14 }} />}
                component={RouterLink} to="/donate/online" clickable
                sx={{
                  bgcolor: 'rgba(212,175,55,0.06)', color: '#fff', height: 26,
                  border: `1px solid ${GOLD}35`, fontWeight: 600, fontSize: '0.76rem',
                  '&:hover': { bgcolor: 'rgba(212,175,55,0.14)' },
                  transition: 'all 200ms',
                }} />
              <Chip label={t('header.bookPooja')} size="small"
                icon={<CardGiftcardIcon sx={{ color: '#fff !important', fontSize: 14 }} />}
                component={RouterLink} to="/poojas/booking" clickable
                sx={{
                  bgcolor: 'rgba(255,255,255,0.03)', color: '#fff', height: 26,
                  border: '1px solid rgba(255,255,255,0.1)', fontWeight: 600, fontSize: '0.76rem',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', borderColor: `${GOLD}25` },
                  transition: 'all 200ms',
                }} />

              {/* Auth */}
              {user ? (
                <>
                  <Chip className="notranslate" translate="no"
                    avatar={
                      user.photoURL
                        ? <Avatar src={user.photoURL} alt={user.displayName || 'User'} sx={{ width: 22, height: 22 }} />
                        : <Avatar sx={{ width: 22, height: 22, bgcolor: GOLD, color: '#1a1a2e', fontSize: '0.65rem', fontWeight: 700 }}>
                            {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
                          </Avatar>
                    }
                    label={user.displayName || user.email?.split('@')[0] || 'User'}
                    size="small" clickable
                    onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                    sx={{
                      bgcolor: 'rgba(212,175,55,0.06)', color: '#fff', height: 26,
                      border: `1px solid ${GOLD}30`, fontWeight: 600, fontSize: '0.76rem',
                      '&:hover': { bgcolor: 'rgba(212,175,55,0.14)', borderColor: GOLD },
                      transition: 'all 200ms',
                    }} />
                  <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={() => setUserMenuAnchor(null)}
                    PaperProps={{ sx: { ...menuPaperSx, minWidth: 180 } }}>
                    <Box className="notranslate" translate="no" sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>{user.displayName || 'User'}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem' }}>{user.email}</Typography>
                    </Box>
                    <MenuItem className="notranslate" translate="no" component={RouterLink} to="/my-devotions" onClick={() => setUserMenuAnchor(null)}
                      sx={{ color: '#fff', py: 0.9, px: 2, fontSize: '0.9rem', '&:hover': { bgcolor: 'rgba(212,175,55,0.08)' } }}>
                      <TempleHinduIcon sx={{ fontSize: 16, color: GOLD, mr: 1 }} /> My Devotions
                    </MenuItem>
                    <MenuItem className="notranslate" translate="no" onClick={async () => { setUserMenuAnchor(null); await logout(); }}
                      sx={{ color: '#fff', py: 0.9, px: 2, fontSize: '0.9rem', '&:hover': { bgcolor: 'rgba(212,175,55,0.08)' } }}>
                      <LogoutIcon sx={{ fontSize: 16, color: GOLD, mr: 1 }} /> Sign Out
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Chip className="notranslate" translate="no" label="Sign In" size="small"
                  icon={<PersonIcon sx={{ color: '#fff !important', fontSize: 14 }} />}
                  onClick={openLoginModal} clickable
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.03)', color: '#fff', height: 26,
                    border: '1px solid rgba(255,255,255,0.1)', fontWeight: 600, fontSize: '0.76rem',
                    '&:hover': { bgcolor: 'rgba(212,175,55,0.08)', borderColor: `${GOLD}25` },
                    transition: 'all 200ms',
                  }} />
              )}
            </Box>
          </Box>

          {/* ═══ MAIN NAV ROW ═══ */}
          <Toolbar disableGutters sx={{
            minHeight: { xs: 64, md: scrolled ? 60 : 68 },
            transition: 'min-height 300ms ease',
            borderBottom: scrolled ? `1px solid ${GOLD}12` : 'none',
          }}>
            {/* Logo */}
            <Box component={RouterLink} to="/"
              sx={{
                textDecoration: 'none', color: '#fff',
                display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1.2 },
                px: { xs: 0.3, sm: 0.5, md: 1 }, py: 0.5, borderRadius: 10,
                transition: 'all 300ms ease',
                minWidth: 0, overflow: 'hidden', flexShrink: 1,
                '&:hover': { '& .logo-img': { boxShadow: `0 0 18px ${GOLD}40` } },
              }}>
              {/* Logo with animated ring */}
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Outer rotating ring */}
                <Box sx={{
                  position: 'absolute',
                  width: { xs: 44, sm: 52, md: scrolled ? 52 : 60 },
                  height: { xs: 44, sm: 52, md: scrolled ? 52 : 60 },
                  borderRadius: '50%',
                  border: `1.5px solid ${GOLD}25`,
                  animation: 'headerRingSpin 10s linear infinite',
                  transition: 'all 300ms ease',
                  '@keyframes headerRingSpin': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                  '&::before': {
                    content: '""', position: 'absolute',
                    top: -2.5, left: '50%', transform: 'translateX(-50%)',
                    width: 5, height: 5, borderRadius: '50%',
                    bgcolor: GOLD, boxShadow: `0 0 8px ${GOLD}80`,
                  },
                }} />
                {/* Pulsing glow ring */}
                <Box sx={{
                  position: 'absolute',
                  width: { xs: 38, sm: 46, md: scrolled ? 46 : 54 },
                  height: { xs: 38, sm: 46, md: scrolled ? 46 : 54 },
                  borderRadius: '50%',
                  border: `1px solid ${GOLD}15`,
                  animation: 'headerGlowPulse 2.5s ease-in-out infinite',
                  transition: 'all 300ms ease',
                  '@keyframes headerGlowPulse': {
                    '0%, 100%': { transform: 'scale(1)', opacity: 0.4 },
                    '50%': { transform: 'scale(1.08)', opacity: 0.9 },
                  },
                }} />
                <Box className="logo-img" component="img"
                  src={`${process.env.PUBLIC_URL}/assets/header_god_image.png`}
                  alt="Muchukunnu Temple"
                  sx={{
                    width: { xs: 34, sm: 42, md: scrolled ? 42 : 50 },
                    height: { xs: 34, sm: 42, md: scrolled ? 42 : 50 },
                    borderRadius: '50%', objectFit: 'cover',
                    border: `2px solid ${scrolled ? GOLD + '80' : GOLD + '40'}`,
                    boxShadow: scrolled ? `0 0 14px ${GOLD}35, 0 0 4px ${GOLD}50` : `0 0 10px ${GOLD}20`,
                    transition: 'all 300ms ease',
                    position: 'relative', zIndex: 1,
                  }} />
              </Box>
              <Box>
                <Typography sx={{
                  fontWeight: 800,
                  fontSize: { xs: 'clamp(0.55rem, 2.2vw, 0.85rem)', md: scrolled ? '0.95rem' : '1.05rem' },
                  letterSpacing: 0.2, lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                  background: `linear-gradient(135deg, #fff 40%, ${GOLD} 100%)`,
                  backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  transition: 'all 300ms ease',
                }}>
                  {t('header.templeName')}
                </Typography>
                <Typography sx={{
                  fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)',
                  fontWeight: 500, letterSpacing: 1.2, textTransform: 'uppercase',
                  display: { xs: 'none', sm: 'block' },
                }}>
                  Sri Kotta-Kovilakam
                </Typography>
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* ── Desktop navigation ── */}
            <Box className="notranslate" translate="no" sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.15 }}>
              {MENU.map((item, idx) => {
                const alignRight = idx >= MENU.length - 2;
                const isOpen = openSubmenu === item.label;
                const active = isActive(item.to);
                return (
                  <Box key={item.label} sx={{ position: 'relative' }}
                    onMouseEnter={() => item.sub && handleMenuEnter(item.label)}
                    onMouseLeave={handleMenuLeave}>
                    <Button
                      component={item.to ? RouterLink : 'button'}
                      to={item.to || '#'}
                      endIcon={item.sub ? <ExpandMoreIcon sx={{
                        fontSize: '14px !important',
                        transition: 'transform 200ms',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      }} /> : undefined}
                      sx={{
                        color: active ? GOLD : '#fff',
                        px: 1.8, py: 0.85, borderRadius: 2,
                        textTransform: 'none', fontWeight: active ? 700 : 600,
                        fontSize: '0.92rem', letterSpacing: 0.15,
                        minWidth: 0,
                        bgcolor: active ? 'rgba(212,175,55,0.08)' : isOpen ? 'rgba(255,255,255,0.04)' : 'transparent',
                        transition: 'all 180ms ease',
                        position: 'relative',
                        '&:hover': {
                          bgcolor: active ? 'rgba(212,175,55,0.12)' : 'rgba(255,255,255,0.06)',
                        },
                        '&::after': active ? {
                          content: '""', position: 'absolute',
                          bottom: 0, left: '50%', transform: 'translateX(-50%)',
                          width: 5, height: 5, borderRadius: '50%', bgcolor: GOLD,
                          boxShadow: `0 0 6px ${GOLD}`,
                        } : {},
                      }}>
                      {item.label}
                    </Button>

                    {/* Dropdown */}
                    {item.sub && (
                      <Box sx={{
                        display: isOpen ? 'block' : 'none',
                        position: 'absolute', top: '100%', mt: 0.4,
                        left: alignRight ? 'auto' : 0, right: alignRight ? 0 : 'auto',
                        zIndex: 1600,
                        animation: isOpen ? 'dropFade 160ms ease-out' : 'none',
                        '@keyframes dropFade': {
                          '0%': { opacity: 0, transform: 'translateY(-4px)' },
                          '100%': { opacity: 1, transform: 'translateY(0)' },
                        },
                      }}>
                        <Paper elevation={20} sx={{
                          bgcolor: 'rgba(8,6,4,0.97)',
                          backdropFilter: 'blur(24px) saturate(160%)',
                          color: '#fff',
                          border: `1px solid ${GOLD}18`,
                          minWidth: 240, p: 0.6, borderRadius: 2.5,
                          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                          overflow: 'hidden', position: 'relative',
                        }}>
                          {/* Top accent */}
                          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                            background: `linear-gradient(90deg, transparent, ${GOLD}70, transparent)` }} />
                          {item.sub.map((s, si) => (
                            <Button key={s.label} component={RouterLink} to={s.to}
                              onClick={() => setOpenSubmenu(null)}
                              sx={{
                                justifyContent: 'flex-start', width: '100%',
                                color: isActive(s.to) ? GOLD : 'rgba(255,255,255,0.85)',
                                borderRadius: 1.5, px: 2, py: 0.9,
                                mb: si < item.sub!.length - 1 ? 0.15 : 0,
                                textTransform: 'none', fontWeight: isActive(s.to) ? 600 : 500,
                                fontSize: '0.9rem',
                                transition: 'all 140ms ease',
                                borderLeft: `2px solid ${isActive(s.to) ? GOLD : 'transparent'}`,
                                bgcolor: isActive(s.to) ? 'rgba(212,175,55,0.06)' : 'transparent',
                                '&:hover': {
                                  bgcolor: 'rgba(212,175,55,0.08)',
                                  borderLeftColor: GOLD,
                                  pl: 2.2,
                                },
                              }}>
                              {s.label}
                            </Button>
                          ))}
                        </Paper>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* ── Mobile action icons ── */}
            <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <IconButton component={RouterLink} to="/donate/online"
                sx={{ color: 'rgba(255,255,255,0.8)', p: 1, '&:hover': { color: GOLD } }}>
                <FavoriteIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton component={RouterLink} to="/poojas/booking"
                sx={{ color: 'rgba(255,255,255,0.8)', p: 1, '&:hover': { color: GOLD } }}>
                <CardGiftcardIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Box onClick={(e) => setLangAnchor(e.currentTarget)} className="notranslate"
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.4,
                  cursor: 'pointer', px: 0.8, py: 0.45,
                  border: `1px solid ${GOLD}25`, borderRadius: '10px',
                  bgcolor: `${GOLD}08`,
                  '&:hover': { bgcolor: `${GOLD}15`, borderColor: `${GOLD}35` },
                  transition: 'all 200ms',
                }}>
                <TranslateIcon sx={{ fontSize: 14, color: GOLD, opacity: 0.85 }} />
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                  {(availableLanguages.find(l => l.code === translateLanguage) || availableLanguages[0]).nativeLabel.slice(0, 6)}
                </Typography>
              </Box>
              {user ? (
                <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ p: 0.6 }}>
                  {user.photoURL
                    ? <Avatar src={user.photoURL} sx={{ width: 28, height: 28 }} />
                    : <Avatar sx={{ width: 28, height: 28, bgcolor: GOLD, color: '#121212', fontSize: '0.72rem', fontWeight: 700 }}>
                        {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
                      </Avatar>}
                </IconButton>
              ) : (
                <IconButton onClick={openLoginModal}
                  sx={{ color: 'rgba(255,255,255,0.8)', p: 1, '&:hover': { color: GOLD } }}>
                  <PersonIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              <IconButton
                onClick={() => drawerOpen ? closeDrawer() : setDrawerOpen(true)}
                sx={{
                  ml: 0.3, color: scrolled ? GOLD : '#fff',
                  border: `1px solid ${scrolled ? GOLD + '30' : 'rgba(255,255,255,0.1)'}`, borderRadius: 1.5,
                  p: 0.8,
                  bgcolor: scrolled ? `${GOLD}08` : 'transparent',
                  '&:hover': { borderColor: `${GOLD}40`, bgcolor: 'rgba(212,175,55,0.05)' },
                }}>
                {drawerOpen ? <CloseIcon sx={{ fontSize: 22 }} /> : <MenuIcon sx={{ fontSize: 22 }} />}
              </IconButton>
            </Box>
          </Toolbar>
        </Box>
      </AppBar>

      {/* Spacer */}
      <Toolbar sx={{ minHeight: { xs: 64, md: 116 } }} />

      {/* ═══ MOBILE DRAWER ═══ */}
      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}
        PaperProps={{ sx: {
          width: { xs: '88vw', sm: '72vw' }, maxWidth: 360,
          bgcolor: 'rgba(10,8,6,0.99)', color: '#fff',
          borderLeft: '1px solid rgba(212,175,55,0.06)',
          pt: '64px',
        } }}>
        {/* Drawer header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.5, borderBottom: '1px solid rgba(212,175,55,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box component="img"
              src={`${process.env.PUBLIC_URL}/assets/header_god_image.png`}
              alt="Temple" sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${GOLD}30` }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: GOLD, lineHeight: 1.1 }}>Menu</Typography>
              <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.3)', letterSpacing: 1 }}>NAVIGATION</Typography>
            </Box>
          </Box>
          <IconButton onClick={closeDrawer} sx={{ color: 'rgba(255,255,255,0.4)', p: 1, '&:hover': { color: GOLD } }}>
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>

        {/* Nav list */}
        <List className="notranslate" translate="no" sx={{ flex: 1, py: 0.5, overflowY: 'auto' }}>
          {MENU.map((item) => (
            <Box key={item.label}>
              <ListItemButton
                component={item.sub ? 'div' : RouterLink}
                {...(!item.sub ? { to: item.to || '#' } : {})}
                onClick={() => {
                  if (item.sub) setMobileOpen(p => ({ ...p, [item.label]: !p[item.label] }));
                  else closeDrawer();
                }}
                sx={{
                  px: 2.5, py: 1.4, borderRadius: 1.5, mx: 0.8, mb: 0.15,
                  bgcolor: isActive(item.to) ? 'rgba(212,175,55,0.05)' : 'transparent',
                  borderLeft: `2px solid ${isActive(item.to) ? GOLD : 'transparent'}`,
                  '&:hover': { bgcolor: 'rgba(212,175,55,0.04)' },
                }}>
                <ListItemText primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.to) ? 700 : 500,
                    fontSize: '0.95rem', color: isActive(item.to) ? GOLD : '#fff',
                  }} />
                {item.sub && (
                  <ExpandMoreIcon sx={{
                    transform: mobileOpen[item.label] ? 'rotate(180deg)' : 'none',
                    transition: 'transform 180ms', color: 'rgba(255,255,255,0.3)', fontSize: 17,
                  }} />
                )}
              </ListItemButton>
              {item.sub && (
                <Collapse in={!!mobileOpen[item.label]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.sub.map((s) => (
                      <ListItemButton key={s.label} component={RouterLink} to={s.to}
                        sx={{
                          pl: 4.5, py: 1.1, mx: 0.8, borderRadius: 1.5,
                          bgcolor: isActive(s.to) ? 'rgba(212,175,55,0.04)' : 'transparent',
                          '&:hover': { bgcolor: 'rgba(212,175,55,0.04)' },
                        }}
                        onClick={closeDrawer}>
                        <Box sx={{ width: 3, height: 3, borderRadius: '50%',
                          bgcolor: isActive(s.to) ? GOLD : 'rgba(255,255,255,0.15)',
                          mr: 1.2, flexShrink: 0 }} />
                        <ListItemText primary={s.label}
                          primaryTypographyProps={{
                            fontSize: '0.88rem', fontWeight: isActive(s.to) ? 600 : 400,
                            color: isActive(s.to) ? GOLD : 'rgba(255,255,255,0.6)',
                          }} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </Box>
          ))}
        </List>

        {/* Drawer footer */}
        <Box className="notranslate" translate="no" sx={{ p: 1.5, borderTop: '1px solid rgba(212,175,55,0.05)' }}>
          {/* Language picker grid */}
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1, px: 0.3 }}>
              <TranslateIcon sx={{ fontSize: 15, color: GOLD }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: 1, textTransform: 'uppercase' }}>
                Language
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {availableLanguages.map((lang) => {
                const isLangActive = translateLanguage === lang.code;
                return (
                  <Box key={lang.code}
                    onClick={() => setTranslateLanguage(lang.code)}
                    sx={{
                      cursor: 'pointer',
                      px: 1, py: 0.5,
                      borderRadius: '8px',
                      bgcolor: isLangActive ? `${GOLD}18` : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${isLangActive ? GOLD + '40' : 'rgba(255,255,255,0.06)'}`,
                      transition: 'all 180ms',
                      '&:hover': { bgcolor: isLangActive ? `${GOLD}22` : 'rgba(255,255,255,0.08)', borderColor: `${GOLD}30` },
                    }}>
                    <Typography sx={{
                      fontSize: '0.68rem', fontWeight: isLangActive ? 700 : 500,
                      color: isLangActive ? GOLD : 'rgba(255,255,255,0.6)',
                      lineHeight: 1.2, whiteSpace: 'nowrap',
                    }}>
                      {lang.nativeLabel}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {user ? (
            <Box sx={{ mb: 1.2, p: 1.2, borderRadius: 2.5, bgcolor: 'rgba(212,175,55,0.03)', border: `1px solid ${GOLD}12` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.2 }}>
                {user.photoURL
                  ? <Avatar src={user.photoURL} sx={{ width: 32, height: 32 }} />
                  : <Avatar sx={{ width: 32, height: 32, bgcolor: GOLD, color: '#121212', fontWeight: 700, fontSize: '0.8rem' }}>
                      {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
                    </Avatar>}
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.88rem', lineHeight: 1.15 }}>{user.displayName || 'User'}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem' }}>{user.email}</Typography>
                </Box>
              </Box>
              <Button fullWidth variant="outlined" startIcon={<TempleHinduIcon sx={{ fontSize: '15px !important' }} />}
                component={RouterLink} to="/my-devotions" size="small"
                sx={{ borderColor: `${GOLD}30`, color: GOLD, fontWeight: 600, fontSize: '0.76rem', mb: 0.6, borderRadius: 1.5,
                  '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' } }}
                onClick={closeDrawer}>My Devotions</Button>
              <Button fullWidth variant="outlined" startIcon={<LogoutIcon sx={{ fontSize: '15px !important' }} />} size="small"
                sx={{ borderColor: 'rgba(255,255,255,0.08)', color: '#fff', fontWeight: 600, fontSize: '0.76rem', borderRadius: 1.5,
                  '&:hover': { borderColor: `${GOLD}25` } }}
                onClick={async () => { closeDrawer(); await logout(); }}>Sign Out</Button>
            </Box>
          ) : (
            <Button fullWidth variant="outlined" startIcon={<PersonIcon />} size="small"
              sx={{ borderColor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 700, mb: 0.6, borderRadius: 1.5,
                '&:hover': { borderColor: `${GOLD}25` } }}
              onClick={() => { closeDrawer(); openLoginModal(); }}>Sign In / Sign Up</Button>
          )}
          <Button fullWidth variant="outlined" startIcon={<NotificationsActiveIcon sx={{ fontSize: '15px !important' }} />} size="small"
            sx={{ borderColor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 700, mb: 0.6, borderRadius: 1.5,
              '&:hover': { borderColor: `${GOLD}25` } }}
            disabled={pushBusy} onClick={handleEnableNotifications}>{t('notifications.enable')}</Button>
          <Button fullWidth variant="outlined" component={RouterLink} to="/poojas/booking"
            startIcon={<CardGiftcardIcon sx={{ fontSize: '15px !important' }} />} size="small"
            sx={{ borderColor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 700, mb: 0.6, borderRadius: 1.5,
              '&:hover': { borderColor: `${GOLD}25` } }}
            onClick={closeDrawer}>{t('header.bookPooja')}</Button>
          <Button fullWidth variant="contained" component={RouterLink} to="/donate/online"
            startIcon={<FavoriteIcon sx={{ fontSize: '15px !important' }} />} size="small"
            sx={{ bgcolor: GOLD, color: '#0a0806', fontWeight: 700, borderRadius: 1.5,
              '&:hover': { bgcolor: '#e0c159' } }}
            onClick={closeDrawer}>Donate</Button>
        </Box>
      </Drawer>
    </>
  );
}
